import { useMemo, useState } from 'react';

import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { FloatingLabel } from '@quick/components/Forms';
import type { ICity } from '@quick/go-api-types/services/cities';
import { cn, filterUniqueObjectsArrayByIdProp } from '@quick/utils';

import { useAppTranslation } from '@/app/i18n/client';
import { ReactSelect as RSelect } from '@/components/core/ReactSelect';
import { useDebounce } from '@/layers/core/deal/services/useDebounce';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { capitalize } from '@/layers/core/deal/utilities/capitalize';

export default function CitySearch<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  rules,
  modal,
  className,
  disabled,
  countryID: country_id,
  requiredMark,
  innerClassName,
}: Omit<ControllerProps<TFieldValues, TName>, 'defaultValue' | 'render'> & {
  modal?: boolean;
  className?: string;
  countryID?: number;
  requiredMark?: boolean;
  innerClassName?: string;
}) {
  const { t } = useAppTranslation(['common']);
  const {
    field,
    fieldState: { error },
  } = useController({ control, name, rules });

  const { data: selectedCity } = useFetch<ICity[]>(
    !!field.value && {
      url: '/services/cities',
      params: {
        city_id: field.value,
        country_id,
      },
    },
    {
      onSuccess(data) {
        const name = data.data.at(0)?.city;
        if (name) set(name);
      },
    }
  );

  const [search, set] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 500);
  const { data: searchedCities } = useFetch<ICity[]>(
    debouncedSearch.length >= 3 && {
      url: '/services/cities',
      params: {
        city: debouncedSearch,
      },
    },
    { keepPreviousData: true }
  );
  const allCities = useMemo(
    () =>
      filterUniqueObjectsArrayByIdProp([
        ...(selectedCity || []),
        ...(searchedCities || []),
      ]) ?? [],
    [searchedCities, selectedCity]
  );

  return (
    <label className={cn('relative', className)}>
      <FloatingLabel
        as="span"
        label={t('common:city')}
        isOnModal={modal}
        required={requiredMark}
      />
      <RSelect
        {...field}
        value={allCities.find(
          ({ id }) => id.toString() === field?.value?.toString()
        )}
        onChange={(e) => field.onChange(e?.id ?? '')}
        className={cn(
          'rs-container h-full min-w-0 rounded',
          !modal && 'nomodal',
          error?.message &&
            `shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.300)]
            dark:shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.500)]`,
          innerClassName
        )}
        classNamePrefix="rs"
        isSearchable
        openMenuOnFocus
        onInputChange={(v, meta) => {
          if (
            // change search input only on user input so we don't trigger a new search on select, blur, etc.
            meta.action === 'input-change'
          )
            set(v);
        }}
        styles={{
          menu: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
          control: (provided) => ({ ...provided, height: '100%' }),
        }}
        isDisabled={disabled}
        options={allCities}
        placeholder={t('common:select')}
        getOptionValue={(c) => `${c.id}`}
        getOptionLabel={(c) =>
          capitalize(
            `${c.name}, ${c.country_state.name}, ${c.country_state.country.name}`
          )
        }
        isLoading={!searchedCities && search.trim().length >= 3}
        components={{ IndicatorSeparator: () => null }}
        noOptionsMessage={({ inputValue }) => {
          if (inputValue.trim().length < 3) return t('common:msg.writeSearch');
          return t('common:msg.noResults');
        }}
      />
    </label>
  );
}
